import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, setToken, getToken } from "@/lib/api";
import { applyTierTheme } from "@/lib/themes";

const AuthCtx = createContext({ user: null, loading: true });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [featureFlags, setFeatureFlags] = useState({ payments_enabled: true });

  useEffect(() => {
    // Beta / free-preview mode flag lives on the server. Public endpoint,
    // no auth required — safe to fetch on every boot.
    api.get("/system/feature-flags")
       .then(({ data }) => setFeatureFlags(data || { payments_enabled: true }))
       .catch(() => setFeatureFlags({ payments_enabled: true }));
  }, []);

  const refresh = useCallback(async () => {
    // ── Native WebView token bridge ─────────────────────────────────
    // When the Expo mobile app opens the web portal in a WebView it
    // injects `?tk=<jwt>` on the boot URL. Stash it so the axios
    // interceptor picks it up on the very first `/auth/me` call.
    // Address-bar cleanup runs *before* storage so a localStorage
    // quota-exceeded error can never leak the JWT into browser history.
    try {
      const url = new URL(window.location.href);
      const bridgeTk = url.searchParams.get("tk");
      if (bridgeTk) {
        url.searchParams.delete("tk");
        window.history.replaceState({}, "", url.toString());
        try { setToken(bridgeTk); } catch { /* storage full — token discarded */ }
      }
    } catch { /* URL parse error — nothing to bridge */ }

    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      applyTierTheme(data?.tier);
    } catch (err) {
      console.warn("[Auth] /auth/me failed, clearing token:", err?.response?.status || err?.message);
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.access_token);
    setUser(data.user);
    applyTierTheme(data.user?.tier);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setToken(data.access_token);
    setUser(data.user);
    applyTierTheme(data.user?.tier);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.warn("[Auth] logout API call failed (proceeding anyway):", err?.message); }
    setToken(null);
    setUser(null);
    applyTierTheme("direction");
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, featureFlags }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
export { formatApiErrorDetail };
